import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { AccountService } from 'src/app/account/services/account.service'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { Subscription } from 'rxjs'
import { ApplicationResource } from 'src/app/shared/resource-viewer-base'
import {
  ResourceConfigDto,
  SyncPairInfo,
  SyncPairResourceConfigDto,
  SyncPairResourceConfigsDto,
} from 'src/app/services/dtos'
import { ResourceType } from 'src/utils/resource-types'
import { handleArpioError } from 'src/utils/handle-error'
import { finalize } from 'rxjs/operators'
import {
  isStatusQuarantineType,
  ResourceStatus,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { isTopmostModal, popModal, pushModal } from 'src/app/shared/modal-dialog/modal-key-stack'

const UNCLEAN_STATUSES = new Set([ResourceStatus.SCAN_INFECTED, ResourceStatus.SCAN_FAILED])

// Resource types that support quarantine, mapped to the resourceConfigType
// token the backend expects for their per-resource config.
const QUARANTINE_CONFIG_TYPE_BY_RESOURCE_TYPE = new Map<ResourceType, string>([
  [ResourceType.ec2Instance, 'ec2Instance'],
  [ResourceType.azureVirtualMachine, 'azureVm'],
])

@Component({
  selector: 'unquarantine-dialog',
  templateUrl: './unquarantine-dialog.component.html',
  styleUrls: ['./unquarantine-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UnquarantineDialogComponent implements OnInit, OnDestroy {
  private unquarantineSubscription: Subscription = null
  private configsSubscription: Subscription = null

  constructor(
    private applicationProviderService: ApplicationProviderService,
    private accountService: AccountService,
    private apiService: ApiService,
    private uiService: UiService,
    private formBuilder: UntypedFormBuilder
  ) {}

  @Input() application: Application
  @Output() close = new EventEmitter<null>()
  private quarantinedResources: Map<string, ApplicationResource> = null
  private resourceConfigs: Map<String, SyncPairResourceConfigDto> = null
  private selected: Set<string> = null
  private understandSelected: boolean = false

  // Esc = Cancel. Listens at the document level so focus anywhere in the
  // modal still dismisses. Topmost-modal gate prevents stacked dialogs from
  // both dismissing on a single Esc.
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!isTopmostModal(this)) return
    if (this.loading) return
    event.preventDefault()
    this.closeDialog()
  }

  ngOnInit() {
    pushModal(this)
    this.quarantinedResources = new Map(
      this.application
        .getRestoreResources()
        .filter((res) => isStatusQuarantineType(res.status))
        .map((res) => [res.resourceArn, res])
    )
    this.selected = new Set<string>()
    this.configsSubscription = this.apiService
      .getSyncPairResourceConfigs(this.accountService.accountId, this.application as SyncPairInfo)
      .pipe(finalize(() => (this.configsSubscription = null)))
      .subscribe(
        (configs: SyncPairResourceConfigDto[]) => {
          this.resourceConfigs = new Map(configs.map((config) => [config.arn, config]))
        },
        handleArpioError(window.location.href, this.apiService, this.uiService, {
          errorContext: { dialogId: 'unquarantine-dialog' },
        })
      )
  }

  get loading(): boolean {
    return subscriptionActive(this.unquarantineSubscription) || subscriptionActive(this.configsSubscription)
  }

  get resources(): ApplicationResource[] {
    return Array.from(this.quarantinedResources.values())
  }

  // Return true if any of the selected resources in the unquarantine dialog were infected or the scan failed
  get isUncleanResourceSelected(): boolean {
    let unclean = false
    for (var arn of this.selected.values()) {
      let res = this.quarantinedResources.get(arn)

      if (UNCLEAN_STATUSES.has(res.status)) {
        unclean = true
      }
    }

    return unclean
  }

  resourceIsChecked(resourceArn: string): boolean {
    return this.selected.has(resourceArn)
  }

  get allResourcesChecked(): boolean {
    return this.resources.every((res) => this.selected.has(res.resourceArn))
  }

  toggleUnderstand(evt) {
    let regexp = new RegExp('\\s*i\\s+understand\\s*', 'i')
    this.understandSelected = regexp.test(evt.target.value)
  }

  get unquarantineUncleanEnabled(): boolean {
    return this.understandSelected
  }

  toggleResourceSelection(resourceArn: string) {
    /*
     * Switches the resource state from true -> false, or vice-versa.
     */
    if (this.resourceIsChecked(resourceArn)) {
      this.selected.delete(resourceArn)
    } else {
      this.selected.add(resourceArn)
    }
  }

  toggleAllResources(): void {
    if (this.allResourcesChecked) {
      return this.resources.forEach((res) => this.selected.delete(res.resourceArn))
    } else {
      return this.resources.forEach((res) => this.selected.add(res.resourceArn))
    }
  }

  get selectedResourceCount(): number {
    return this.selected.size
  }
  get ransomwareDocsUrl(): string {
    return this.uiService.ransomwareDocsUrl
  }

  get unquarantineAllowed(): boolean {
    // start off with allow unquarantine if any record is selected
    let allowed = this.selectedResourceCount > 0

    //but disallow if an "unclean" resource is selected and user doesn't ack that
    if (this.isUncleanResourceSelected && !this.unquarantineUncleanEnabled) {
      allowed = false
    }

    return allowed
  }

  get unquarantineConfigs(): SyncPairResourceConfigsDto {
    const dtos: SyncPairResourceConfigDto[] = Array.from(this.selected)
      .filter((arn) => QUARANTINE_CONFIG_TYPE_BY_RESOURCE_TYPE.has(this.quarantinedResources.get(arn).resourceType))
      .map((resArn) => {
        if (this.resourceConfigs.has(resArn)) {
          const existingConfig = this.resourceConfigs.get(resArn).resourceConfig as ResourceConfigDto
          existingConfig.quarantined = false
          return this.resourceConfigs.get(resArn)
        } else {
          const resourceType = this.quarantinedResources.get(resArn).resourceType
          const config = {
            resourceConfigType: QUARANTINE_CONFIG_TYPE_BY_RESOURCE_TYPE.get(resourceType),
            quarantined: false,
          } as ResourceConfigDto
          return { arn: resArn, resourceConfig: config } as SyncPairResourceConfigDto
        }
      })
    return { configs: dtos }
  }
  onUnquarantineClick() {
    this.unquarantineSubscription = this.apiService
      .setSyncPairResourceConfigs(
        this.accountService.accountId,
        this.application as SyncPairInfo,
        this.unquarantineConfigs
      )
      .pipe(finalize(() => (this.unquarantineSubscription = null)))
      .subscribe(
        (result: SyncPairResourceConfigsDto) => {
          const [succeeded, failed] = [[], []]
          result.configs.forEach((dto) => (dto.resourceConfig.quarantined == false ? succeeded : failed).push(dto.arn))
          succeeded.forEach((arn) => this.quarantinedResources.delete(arn))
          this.application.updateRestoreResourceStatus(ResourceStatus.SUCCEEDED, new Set(succeeded))
          this.closeDialog()
          if (failed.length > 0) {
            throw new Error(`Unable to unquarantine resources ${failed.join(', ')}`)
          }
        },
        handleArpioError(window.location.href, this.apiService, this.uiService, {
          errorContext: { dialogId: 'unquarantine-dialog' },
        })
      )
  }

  closeDialog() {
    this.close.emit(null)
  }

  ngOnDestroy() {
    popModal(this)
  }

  // Has an infected or unscanned resource been selected??
  get uncleanResourceSelected(): number {
    return this.selected.size
  }
}
